import React, { useState } from "react";
import { ChevronDown, Edit2, HelpCircle, User } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Link } from "react-router-dom";
import { useContext } from "react";
import GlobalContext from "../context/GlobalContext";
import Navbar from "../components/shared/Navbar";

const Profile = () => {
  const [isEditing, setIsEditing] = useState(null);
  const { userInfo } = useContext(GlobalContext);
  const [profileData, setProfileData] = useState({
    fullName: "John Doe",
    email: "johndoe@gmail.com",
    gender: "Male",
    birthday: "Dd/Mm/Year",
    contactPhone: "+1234567890",
  });

  const handleEdit = (field) => {
    setIsEditing(field);
  };

  const handleSave = (field, value) => {
    setProfileData((prev) => ({ ...prev, [field]: value }));
    setIsEditing(null);
  };

  const handleCancel = () => {
    setIsEditing(null);
  };

  const EditableField = ({ field, label, value, type = "text" }) => {
    const [editValue, setEditValue] = useState(value);

    if (isEditing === field) {
      return (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 border-b border-gray-100 gap-2 sm:gap-0">
          <Label className="text-gray-700 font-medium sm:min-w-[140px] mb-2 sm:mb-0">
            {label}
          </Label>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-1 sm:max-w-md">
            <Input
              type={type}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="flex-1 w-full"
              autoFocus
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => handleSave(field, editValue)}
                className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none"
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCancel}
                className="flex-1 sm:flex-none"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 border-b border-gray-100 group hover:bg-gray-50 transition-colors px-2 -mx-2 rounded gap-2 sm:gap-0">
        <Label className="text-gray-700 font-medium sm:min-w-[140px] mb-1 sm:mb-0">
          {label}
        </Label>
        <div className="flex items-center gap-2 flex-1 sm:max-w-md">
          <span className="text-gray-900 flex-1 break-words">{value}</span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleEdit(field)}
            className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity p-1 h-8 w-8 flex-shrink-0"
          >
            <Edit2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Page Title */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Overview
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-2">
            <Card className="shadow-sm">
              <CardContent className="p-4 sm:p-6">
                {/* Profile Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-4 sm:pb-6 border-b border-gray-100 mb-4 sm:mb-6 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="h-6 w-6 text-gray-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
                        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                          {profileData.fullName}
                        </h2>
                        <Badge
                          variant="secondary"
                          className="bg-green-100 text-green-800 hover:bg-green-100 w-fit"
                        >
                          PRO
                        </Badge>
                      </div>
                      <p className="text-gray-600 text-sm sm:text-base truncate">
                        {profileData.email}
                      </p>
                    </div>
                  </div>

                  <div className="text-left sm:text-right flex-shrink-0">
                    <p className="text-base sm:text-lg font-semibold text-gray-900">
                      $490.00 per Year
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500">
                      Autopay on Dec 01, 2025
                    </p>
                  </div>
                </div>

                {/* Profile Fields */}
                <div className="space-y-0">
                  <EditableField
                    field="email"
                    label="Email"
                    value={profileData.email}
                    type="email"
                  />
                  <EditableField
                    field="fullName"
                    label="Full Name"
                    value={profileData.fullName}
                  />
                  <EditableField
                    field="gender"
                    label="Gender"
                    value={profileData.gender}
                  />
                  <EditableField
                    field="birthday"
                    label="Birthday"
                    value={profileData.birthday}
                  />
                  <EditableField
                    field="contactPhone"
                    label="Contact phone"
                    value={profileData.contactPhone}
                    type="tel"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Side Panel */}
          <div className="lg:col-span-1">
            <Card className="shadow-sm">
              <CardContent className="p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
                  Account Status
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm sm:text-base">
                      Plan
                    </span>
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-xs sm:text-sm">
                      Pro
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm sm:text-base">
                      Status
                    </span>
                    <span className="text-green-600 font-medium text-sm sm:text-base">
                      Active
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm sm:text-base">
                      Next Payment
                    </span>
                    <span className="text-gray-900 font-medium text-sm sm:text-base">
                      Dec 01, 2025
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;
